import type { NewIngredient } from '../domain/types.js';

/**
 * Split a free-text ingredient line into quantity, unit, and name.
 *
 * Schema.org `recipeIngredient` is almost always an unstructured string
 * ("250g ricotta", "1/2 tsp salt"). Platewell stores those parts separately so
 * the editor and the recipe view can show amount vs name.
 *
 * This is best-effort: if the line does not start with a quantity, the whole
 * string is kept as the name.
 */
export function parseIngredientLine(line: string): NewIngredient {
  const cleaned = line
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^[-*•]\s+/, '');
  if (cleaned === '') return { name: '' };

  const parts = splitAmount(cleaned);
  if (parts == null) return { name: cleaned };

  const name = parts.name;
  if (name === '' && parts.quantity === '' && parts.unit === '') return { name: cleaned };

  return toIngredient(parts.quantity, parts.unit, name);
}

/**
 * Turn a unit string into the short form used in the editor ("grams" → "g"),
 * or return the trimmed original when it is not a known unit.
 */
export function canonicalizeUnit(raw: string): string {
  const trimmed = raw.trim().replace(/\.$/, '');
  if (trimmed === '') return '';
  const code = UNIT_CODES[trimmed.toUpperCase()];
  if (code != null) return code;
  const match = matchUnit(trimmed);
  if (match != null && match.length >= trimmed.length) return match.canonical;
  return trimmed;
}

function toIngredient(quantity: string, unit: string, name: string): NewIngredient {
  return {
    name,
    ...(quantity !== '' ? { quantity } : {}),
    ...(unit !== '' ? { unit } : {}),
  };
}

interface SplitAmount {
  quantity: string;
  unit: string;
  name: string;
}

function splitAmount(line: string): SplitAmount | null {
  const approx = /^(?:about|approx(?:imately|\.)?|roughly|around|circa)\s+/i.exec(line);
  const start = approx == null ? 0 : approx[0].length;

  const article = matchIndefiniteMeasure(line.slice(start));
  const first = article ?? matchSingleQuantity(line.slice(start));
  if (first == null) return null;

  let quantity = first.quantity;
  let rest = first.rest;

  if (/^%/.test(rest)) return null;
  // "1-inch ginger" is a size, not a 1 to inch range.
  if (/^[-–—][A-Za-z]/.test(rest)) return null;

  rest = rest.trimStart();
  const range = matchRangeSuffix(rest);
  if (range != null) {
    quantity = `${quantity}-${range.quantity}`;
    rest = range.rest;
  } else {
    const times = matchTimesSuffix(rest);
    if (times != null) {
      quantity = times.quantity === '' ? quantity : `${quantity} x ${times.quantity}`;
      rest = times.rest;
    }
  }

  rest = rest.trim();
  const paren = takeLeadingParen(rest);
  rest = paren.rest;

  const unitMatch = matchUnit(rest);
  let unit = '';
  if (unitMatch != null) {
    unit = unitMatch.canonical;
    rest = rest.slice(unitMatch.length);
  }

  rest = rest.trim();
  if (unit !== '') rest = rest.replace(/^(?:of\s+|,)\s*/i, '').trim();
  const name = [paren.prefix, rest].filter((part) => part !== '').join(' ');
  return { quantity, unit, name };
}

interface QuantityMatch {
  quantity: string;
  rest: string;
}

const VULGAR = '¼½¾⅓⅔⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞⅐⅑⅒';
const VULGAR_TO_ASCII: Record<string, string> = {
  '¼': '1/4',
  '½': '1/2',
  '¾': '3/4',
  '⅓': '1/3',
  '⅔': '2/3',
  '⅕': '1/5',
  '⅖': '2/5',
  '⅗': '3/5',
  '⅘': '4/5',
  '⅙': '1/6',
  '⅚': '5/6',
  '⅛': '1/8',
  '⅜': '3/8',
  '⅝': '5/8',
  '⅞': '7/8',
  '⅐': '1/7',
  '⅑': '1/9',
  '⅒': '1/10',
};

function matchIndefiniteMeasure(text: string): QuantityMatch | null {
  const match = /^(?:an?\s+)(?=pinch|dash|handful|sprig|bunch|clove|splash|drop)\b/i.exec(text);
  if (match == null) return null;
  return { quantity: '1', rest: text.slice(match[0].length) };
}

function matchSingleQuantity(text: string): QuantityMatch | null {
  let match = /^(\d+)\s+(\d+)\s*\/\s*(\d+)/.exec(text);
  if (match != null) {
    return { quantity: `${match[1]} ${match[2]}/${match[3]}`, rest: text.slice(match[0].length) };
  }

  match = new RegExp(`^(\\d+)([${VULGAR}])`).exec(text);
  if (match != null) {
    return {
      quantity: `${match[1]} ${vulgarToAscii(match[2]!)}`,
      rest: text.slice(match[0].length),
    };
  }

  match = /^(\d+)\s*\/\s*(\d+)/.exec(text);
  if (match != null) {
    return { quantity: `${match[1]}/${match[2]}`, rest: text.slice(match[0].length) };
  }

  match = new RegExp(`^([${VULGAR}])`).exec(text);
  if (match != null) {
    return { quantity: vulgarToAscii(match[1]!), rest: text.slice(match[0].length) };
  }

  match = /^(\d{1,3}(?:,\d{3})+)(?:\.(\d+))?/.exec(text);
  if (match != null) {
    const int = match[1]!.replace(/,/g, '');
    const quantity = match[2] != null ? `${int}.${match[2]}` : int;
    return { quantity, rest: text.slice(match[0].length) };
  }

  match = /^(\d+),(\d{1,2})(?!\d)/.exec(text);
  if (match != null) {
    return { quantity: `${match[1]}.${match[2]}`, rest: text.slice(match[0].length) };
  }

  match = /^(\d+)\.(\d+)/.exec(text);
  if (match != null) {
    return { quantity: `${match[1]}.${match[2]}`, rest: text.slice(match[0].length) };
  }

  match = /^(\d+)/.exec(text);
  if (match != null) {
    return { quantity: match[1]!, rest: text.slice(match[0].length) };
  }

  return null;
}

function vulgarToAscii(symbol: string): string {
  return VULGAR_TO_ASCII[symbol] ?? symbol;
}

function matchRangeSuffix(rest: string): QuantityMatch | null {
  const sep = /^(?:[-–—]\s*|to\s+)/i.exec(rest);
  if (sep == null) return null;
  return matchSingleQuantity(rest.slice(sep[0].length));
}

function matchTimesSuffix(rest: string): QuantityMatch | null {
  const sep = /^[x×](?:\s+|(?=\d))/i.exec(rest);
  if (sep == null) return null;
  const after = rest.slice(sep[0].length);
  const second = matchSingleQuantity(after);
  if (second != null) return second;
  return { quantity: '', rest: after };
}

function takeLeadingParen(text: string): { prefix: string; rest: string } {
  if (!text.startsWith('(')) return { prefix: '', rest: text };
  const end = text.indexOf(')');
  if (end === -1) return { prefix: '', rest: text };
  return { prefix: text.slice(0, end + 1), rest: text.slice(end + 1).trimStart() };
}

interface UnitAlias {
  alias: string;
  canonical: string;
  caseSensitive?: boolean;
}

const UNIT_GROUPS: ReadonlyArray<{ canonical: string; aliases: readonly string[] }> = [
  { canonical: 'fl oz', aliases: ['fluid ounces', 'fluid ounce', 'fl. oz', 'fl oz', 'floz'] },
  { canonical: 'tsp', aliases: ['teaspoons', 'teaspoon', 'tsps', 'tsp'] },
  {
    canonical: 'tbsp',
    aliases: ['tablespoons', 'tablespoon', 'tbsps', 'tbsp', 'tbs', 'tbls', 'tbl'],
  },
  { canonical: 'g', aliases: ['grams', 'gram', 'gms', 'grms', 'gr', 'g'] },
  { canonical: 'kg', aliases: ['kilograms', 'kilogram', 'kgs', 'kg'] },
  { canonical: 'mg', aliases: ['milligrams', 'milligram', 'mg'] },
  {
    canonical: 'ml',
    aliases: ['millilitres', 'milliliters', 'millilitre', 'milliliter', 'mls', 'ml', 'cc'],
  },
  { canonical: 'l', aliases: ['litres', 'liters', 'litre', 'liter', 'l'] },
  { canonical: 'dl', aliases: ['decilitres', 'deciliters', 'decilitre', 'deciliter', 'dl'] },
  { canonical: 'cl', aliases: ['centilitres', 'centiliters', 'centilitre', 'centiliter', 'cl'] },
  { canonical: 'oz', aliases: ['ounces', 'ounce', 'oz'] },
  { canonical: 'lb', aliases: ['pounds', 'pound', 'lbs', 'lb'] },
  { canonical: 'cup', aliases: ['cups', 'cup'] },
  { canonical: 'pt', aliases: ['pints', 'pint', 'pt'] },
  { canonical: 'qt', aliases: ['quarts', 'quart', 'qt'] },
  { canonical: 'gal', aliases: ['gallons', 'gallon', 'gal'] },
  { canonical: 'clove', aliases: ['cloves', 'clove'] },
  { canonical: 'slice', aliases: ['slices', 'slice'] },
  { canonical: 'pinch', aliases: ['pinches', 'pinch'] },
  { canonical: 'dash', aliases: ['dashes', 'dash'] },
  { canonical: 'handful', aliases: ['handfuls', 'handful'] },
  { canonical: 'bunch', aliases: ['bunches', 'bunch'] },
  { canonical: 'sprig', aliases: ['sprigs', 'sprig'] },
  { canonical: 'can', aliases: ['cans', 'can'] },
  { canonical: 'tin', aliases: ['tins', 'tin'] },
  { canonical: 'stick', aliases: ['sticks', 'stick'] },
  { canonical: 'package', aliases: ['packages', 'package', 'pkgs', 'pkg'] },
  { canonical: 'piece', aliases: ['pieces', 'piece'] },
  { canonical: 'head', aliases: ['heads', 'head'] },
  { canonical: 'leaf', aliases: ['leaves', 'leaf'] },
  { canonical: 'sheet', aliases: ['sheets', 'sheet'] },
  { canonical: 'bag', aliases: ['bags', 'bag'] },
  { canonical: 'jar', aliases: ['jars', 'jar'] },
  { canonical: 'bottle', aliases: ['bottles', 'bottle'] },
  { canonical: 'box', aliases: ['boxes', 'box'] },
  { canonical: 'packet', aliases: ['packets', 'packet', 'sachets', 'sachet'] },
  { canonical: 'scoop', aliases: ['scoops', 'scoop'] },
  { canonical: 'splash', aliases: ['splashes', 'splash'] },
  { canonical: 'drop', aliases: ['drops', 'drop'] },
];

const UNIT_ALIASES: readonly UnitAlias[] = [
  ...UNIT_GROUPS.flatMap((group) =>
    group.aliases.map((alias) => ({ alias, canonical: group.canonical })),
  ),
  { alias: 't', canonical: 'tsp', caseSensitive: true },
  { alias: 'T', canonical: 'tbsp', caseSensitive: true },
  { alias: 'c', canonical: 'cup' },
];

const UNIT_CODES: Record<string, string> = {
  GRM: 'g',
  KGM: 'kg',
  MGM: 'mg',
  MLT: 'ml',
  LTR: 'l',
  DLT: 'dl',
  ONZ: 'oz',
  LBR: 'lb',
  GLL: 'gal',
};

function matchUnit(text: string): { canonical: string; length: number } | null {
  let best: { canonical: string; length: number } | null = null;
  for (const { alias, canonical, caseSensitive } of UNIT_ALIASES) {
    const slice = text.slice(0, alias.length);
    const matches = caseSensitive ? slice === alias : slice.toLowerCase() === alias.toLowerCase();
    if (!matches) continue;
    const after = text[alias.length];
    if (after != null && /[A-Za-z]/.test(after)) continue;
    let length = alias.length;
    if (after === '.') length += 1;
    if (best == null || length > best.length) best = { canonical, length };
  }
  return best;
}
